import type { ReactNode } from 'react';
import { Fragment } from 'react';

import { SubText, Text } from '@procurify/ui'

import { StatusCardLegacyContentTableStyled } from './styles/StatusCardLegacyContentTableStyled'
import { StatusCardNamedGridStyled as NamedGrid } from './styles/StatusCardNamedGridStyled'

type BaseContentProps = {
  /**
   * Should be used for primitive types like strings or numbers,
   * or components that can be rendered inside a `<SubText>` component.
   */
  value?: ReactNode
  /**
   * Should be used for complex React components.
   * It is intended for custom rendering logic or when the content is more than just simple text.
   * The content will be rendered as-is, so it can include any valid React elements.
   */
  render?: ReactNode
  justifyContent?: 'flex-start' | 'center' | 'flex-end'
}

type HeaderContentProps = BaseContentProps & {
  label?: ReactNode
}

type DataContentProps = BaseContentProps & {
  label: ReactNode
}

export interface IStatusCardLegacyContentTableProps {
  id?: string
  headerLeft?: HeaderContentProps
  headerRight?: HeaderContentProps
  dataLeft?: DataContentProps[]
  dataRight?: DataContentProps[]
}

const getGridId = (id: string = '', name: string, index?: number) => {
  return `${id ? `${id}-` : ''}${name}${index !== undefined ? `-${index}` : ''}`
}

export const StatusCardLegacyContentTable = (
  props: IStatusCardLegacyContentTableProps
) => {
  const { id, headerLeft, headerRight, dataLeft = [], dataRight = [] } = props

  const renderHeader = (
    header: HeaderContentProps | undefined,
    side: 'left' | 'right'
  ) => {
    const gridId = getGridId(id, `${side}-header-value`)

    return (
      <>
        {header?.label && (
          <NamedGrid
            gridName={`${side}HeaderLabel`}
            type='label'
            justifyContent={header.justifyContent}
          >
            <label id={gridId}>
              <Text textEllipsis>{header.label}</Text>
            </label>
          </NamedGrid>
        )}
        <NamedGrid
          gridName={`${side}HeaderValue`}
          type='value'
          justifyContent={header?.justifyContent}
        >
          {header?.render && !header?.value && (
            <div aria-labelledby={gridId}>{header.render}</div>
          )}
          {header?.value && !header?.render && (
            <Text textEllipsis bold aria-labelledby={gridId}>
              {header.value}
            </Text>
          )}
        </NamedGrid>
      </>
    )
  }

  const renderData = (data: DataContentProps[], side: 'left' | 'right') => {
    return data.map((item, index) => {
      const gridId = getGridId(id, `${side}-body-value`, index)

      return (
        <Fragment key={index}>
          <NamedGrid
            gridName={`${side}Label${index}`}
            type='label'
            justifyContent={item?.justifyContent}
          >
            <label id={gridId}>
              <SubText textEllipsis>{item.label}</SubText>
            </label>
          </NamedGrid>
          <NamedGrid
            gridName={`${side}Value${index}`}
            type='value'
            justifyContent={item?.justifyContent}
          >
            {item.render ? (
              <div aria-labelledby={gridId}>{item.render}</div>
            ) : (
              <SubText textEllipsis aria-labelledby={gridId}>
                {item.value}
              </SubText>
            )}
          </NamedGrid>
        </Fragment>
      );
    });
  }

  return (
    <StatusCardLegacyContentTableStyled {...props}>
      {renderHeader(headerLeft, 'left')}
      {renderHeader(headerRight, 'right')}
      {renderData(dataLeft, 'left')}
      {renderData(dataRight, 'right')}
    </StatusCardLegacyContentTableStyled>
  )
}

StatusCardLegacyContentTable.displayName = 'StatusCardLegacyContentTable'
